// Servicio para interactuar con la API de Blog PHP

// Ajusta esta URL base si tu API está en un dominio/puerto diferente
// o si la ruta cambia en producción.
//const API_BASE_URL = 'https://sokar.es/api/blog.php'; // Ruta relativa a la API local
import { API_BASE_URL } from '../config';

// URL base para las llamadas a la API del blog
const BLOG_API_URL = `${API_BASE_URL}/blog.php`;

/**
 * Maneja la respuesta de la API, lanzando un error si no es exitosa.
 * @param {Response} response La respuesta de fetch.
 * @returns {Promise<any>} El cuerpo de la respuesta JSON.
 * @throws {Error} Si la respuesta no es ok.
 */
async function handleResponse(response) {
  if (!response.ok) {
    let errorData;
    try {
        errorData = await response.json();
    } catch (e) {
        errorData = { error: `Error ${response.status}: ${response.statusText}` };
    }
    throw new Error(errorData.error || `Error ${response.status}: ${response.statusText}`);
  }
  // Si la respuesta es OK pero no tiene contenido (ej. DELETE exitoso)
  if (response.status === 204 || response.headers.get('content-length') === '0') {
      return { success: true }; // O devuelve null o lo que sea apropiado
  }
  return response.json();
}

/**
 * Obtiene los encabezados de autorización con el token JWT.
 * @returns {object} Objeto de encabezados.
 */
function getAuthHeaders() {
  const token = localStorage.getItem('jwt_token');
  return token ? { 'Authorization': `Bearer ${token}` } : {};
}

// --- Funciones Públicas ---

/**
 * Obtiene una lista paginada de posts del blog públicos.
 * @param {number} [page=1] Número de página.
 * @param {number} [limit=10] Número de posts por página.
 * @returns {Promise<{posts: Array<object>, pagination: object}>}
 */
export async function getBlogPosts(page = 1, limit = 10) {
  const url = `${BLOG_API_URL}?action=list&page=${page}&limit=${limit}`;
  const response = await fetch(url);
  return handleResponse(response);
}

/**
 * Obtiene los detalles completos de un post público por su slug.
 * @param {string} slug El slug del post.
 * @returns {Promise<object>} Los detalles del post.
 */
export async function getBlogPostBySlug(slug) {
  if (!slug) throw new Error('Se requiere el slug del post.');
  const url = `${BLOG_API_URL}?action=detail&slug=${encodeURIComponent(slug)}`;
  const response = await fetch(url);
  return handleResponse(response);
}

/**
 * Obtiene los detalles completos de un post público por su ID.
 * @param {number} id El ID del post.
 * @returns {Promise<object>} Los detalles del post.
 */
export async function getBlogPostById(id) {
    if (!id) throw new Error('Se requiere el ID del post.');
    const url = `${BLOG_API_URL}?action=detail&id=${id}`;
    const response = await fetch(url);
    return handleResponse(response);
}

// --- Funciones de Administración ---

/**
 * Obtiene TODAS las entradas del blog (publicadas y borradores).
 * @returns {Promise<Array<object>>} Lista de todos los posts.
 */
export async function getAllBlogPosts() {
    const url = `${BLOG_API_URL}?action=list_all`;
    const response = await fetch(url, {
        headers: getAuthHeaders()
    });
    return handleResponse(response);
}

/**
 * Obtiene los detalles completos de un post por su ID (para admin).
 * @param {number} postId El ID del post.
 * @returns {Promise<object>} Los detalles completos del post.
 */
export async function getAdminBlogPostDetails(postId) {
    if (!postId) throw new Error('Se requiere el ID del post.');
    const url = `${BLOG_API_URL}?action=detail&id=${postId}`;
    const response = await fetch(url, {
        headers: getAuthHeaders()
    });
    return handleResponse(response);
}

/**
 * Crea una nueva entrada de blog.
 * @param {object} postData Datos del post (title, summary, content, image_url, publish_now).
 * @returns {Promise<object>} Respuesta de la API (ej. { success: true, id: newId, slug: newSlug }).
 */
export async function createBlogPost(postData) {
     const url = `${BLOG_API_URL}?action=create`;
     const response = await fetch(url, {
         method: 'POST',
         headers: {
             'Content-Type': 'application/json',
             ...getAuthHeaders()
         },
         body: JSON.stringify(postData),
     });
     return handleResponse(response);
 }

/**
 * Actualiza una entrada de blog existente.
 * @param {number} postId El ID del post a actualizar.
 * @param {object} postData Datos del post a actualizar (title, summary, content, list_image_id, detail_image_id, publish_now).
 * @returns {Promise<object>} Respuesta de la API (ej. { success: true, slug: updatedSlug }).
 */
export async function updateBlogPost(postId, postData) {
    if (!postId) throw new Error('Se requiere el ID del post a actualizar.');
     const url = `${BLOG_API_URL}?action=update`;
     const response = await fetch(url, {
         method: 'POST',
         headers: {
             'Content-Type': 'application/json',
             ...getAuthHeaders()
         },
         body: JSON.stringify({ id: postId, ...postData }),
     });
     return handleResponse(response);
 }

/**
 * Elimina una entrada de blog.
 * @param {number} postId El ID del post a eliminar.
 * @returns {Promise<object>} Respuesta de la API (ej. { success: true }).
 */
export async function deleteBlogPost(postId) {
    if (!postId) throw new Error('Se requiere el ID del post a eliminar.');
     const url = `${BLOG_API_URL}?action=delete`;
     const response = await fetch(url, {
         method: 'POST',
         headers: {
             'Content-Type': 'application/json',
             ...getAuthHeaders()
         },
         body: JSON.stringify({ id: postId }),
     });
     return handleResponse(response);
 }